import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@fiverr-private/button';
import { Typography } from '@fiverr-private/typography';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Control, FormElement, FormHelperText } from '@fiverr-private/forms';
import pathfinder from '@fiverr-private/pathfinder';
import { getEventName, getMixpanelEventName, getGrafanaEventName } from '../../utils/eventHelpers';
import { ACTIVATION_CODE_LENGTH, ActivationErrorType, RESEND_SUCCESS_DURATION_MS } from '../../constants';
import { globalBigQueryReporter, globalMixpanelReporter, globalGrafanaReporter } from '../../events';
import { CodeInput } from '../shared/CodeInput';
import { IdentificationFormHeader } from '../shared/IdentificationFormHeader';
import { IdentificationFormFooter } from '../shared/IdentificationFormFooter';
import { IdentificationOnboardingFormFooter } from '../shared/IdentificationOnboardingFormFooter';
import { Variant } from '../../types';
import { appTranslate } from '../../i18n';
import { useActivateWithCode } from './hooks/useActivateWithCode';
import Resend from './Resend';
import * as classes from './style.ve.css';

interface CodeActivationStepProps {
    email: string;
    variant?: Variant;
    isSkippable?: boolean;
    postActivationDelay?: number;
    onGoToChangeEmail?: () => void;
    onGoToHomepage?: () => void;
    onSkip?: () => void;
    onError?: (error: ActivationErrorType) => void;
    onSuccess?: () => void;
    initialFocusRef?: React.RefObject<HTMLInputElement>;
    mixpanelEnrichment?: Partial<Record<string, string | number | boolean>>;
    bigQueryEnrichment?: Partial<Record<string, string | number | boolean>>;
}

export const CodeActivationStep = ({
    email,
    variant = 'default',
    isSkippable = true,
    postActivationDelay = 0,
    onSkip,
    onSuccess,
    onError,
    onGoToChangeEmail,
    onGoToHomepage,
    initialFocusRef,
    mixpanelEnrichment = {},
    bigQueryEnrichment = {},
}: CodeActivationStepProps) => {
    const isShown = useRef(false);
    const [isCheckingCode, setIsCheckingCode] = useState(false);
    const [showResendSuccess, setShowResendSuccess] = useState(false);
    const [activationError, setActivationError] = useState<ActivationErrorType | null>(null);
    const [activationCode, setActivationCode] = useState('');

    useEffect(() => {
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bigQueryEnrichment);
        if (!isShown.current) {
            globalMixpanelReporter.send(getMixpanelEventName(variant, 'show'));
            globalBigQueryReporter.send(getEventName(variant, 'show'));
            globalGrafanaReporter.count(getGrafanaEventName(variant, 'show'));
            isShown.current = true;
        }
    }, [mixpanelEnrichment, bigQueryEnrichment, variant]);

    useEffect(() => {
        if (!showResendSuccess) {
            return;
        }

        if (isCheckingCode) {
            setShowResendSuccess(false);
        }

        setTimeout(() => setShowResendSuccess(false), RESEND_SUCCESS_DURATION_MS);
    }, [showResendSuccess, isCheckingCode, setShowResendSuccess]);

    const activate = useActivateWithCode({
        postActivationDelay,
        onSuccess,
        onError,
        setError: setActivationError,
        setIsCheckingCode,
        variant,
    });

    const handleSkip = () => {
        globalMixpanelReporter.send(getMixpanelEventName(variant, 'skip'));
        globalBigQueryReporter.send(getEventName(variant, 'skip'));
        globalGrafanaReporter.count(getGrafanaEventName(variant, 'skip'));
        onSkip?.();
    };

    const handleGoToHomepage = () => {
        globalMixpanelReporter.send(getMixpanelEventName(variant, 'homepage_cta'));
        globalBigQueryReporter.send(getEventName(variant, 'homepage_cta'));
        globalGrafanaReporter.count(getGrafanaEventName(variant, 'homepage_cta'));
        onGoToHomepage?.();
    };

    const { title, subtitle } = useMemo(
        () => ({
            title: {
                go: appTranslate('activation.title'),
                default: appTranslate('activation.title'),
                purchase: appTranslate('activation.purchase_title'),
                team: appTranslate('activation.team_title'),
                onboarding: appTranslate('activation.title'),
            } satisfies Record<Variant, string>,
            subtitle: {
                go: appTranslate('activation.subtitle'),
                default: appTranslate('activation.subtitle'),
                purchase: appTranslate('activation.purchase_subtitle'),
                team: appTranslate('activation.team_subtitle'),
                onboarding: appTranslate('activation.subtitle'),
            } satisfies Record<Variant, string>,
        }),
        []
    );

    const showChangeEmail = variant !== 'team';
    const isOnboarding = variant === 'onboarding';

    const subtitleText = isOnboarding ? `${subtitle[variant]} ${email}.` : subtitle[variant];

    const skipButton = isSkippable && onSkip && (
        <Button onClick={handleSkip} variant="ghost" className={classes.skipButton}>
            {appTranslate<string>(`activation.remind_me_later`)}
        </Button>
    );

    const submitButton = (
        <Button
            onClick={() => activate(activationCode)}
            loading={isCheckingCode}
            disabled={activationCode.length !== ACTIVATION_CODE_LENGTH}
            marginLeft="auto"
            fullWidth={variant === 'team'}
        >
            {variant === 'team'
                ? appTranslate<string>('activation.team_submit')
                : appTranslate<string>('general.submit')}
        </Button>
    );

    return (
        <Container display="flex" direction="column" height="100%" minHeight={0}>
            <Container minHeight={0} overflowY="auto" maxWidth={isOnboarding ? '550px' : undefined}>
                <IdentificationFormHeader
                    variant={variant}
                    title={title[variant]}
                    subTitle={
                        <>
                            {subtitleText}

                            <Stack alignItems="baseline" gap="2" wrap="wrap">
                                {!isOnboarding && (
                                    <Typography color="grey_1100" fontWeight="semibold">
                                        {email}.
                                    </Typography>
                                )}

                                {showChangeEmail && (
                                    <Typography
                                        size="b_sm"
                                        cursor="pointer"
                                        color="grey_1000"
                                        fontWeight="semibold"
                                        onClick={onGoToChangeEmail}
                                        whiteSpace="nowrap"
                                    >
                                        (
                                        <Typography as="span" decoration="underline">
                                            {appTranslate<string>('activation.change_email_cta')}
                                        </Typography>
                                        )
                                    </Typography>
                                )}
                            </Stack>
                        </>
                    }
                />

                <Stack direction="column" gap="4" paddingTop="14" minHeight={0} overflowY="auto" padding="px">
                    <Control isError={!!activationError}>
                        <FormElement>
                            <CodeInput
                                autoFocus
                                codeLength={ACTIVATION_CODE_LENGTH}
                                isError={!!activationError}
                                onChange={(value) => setActivationCode(value)}
                                onSubmit={(value) => activate(value)}
                                firstInputRef={initialFocusRef}
                            />
                        </FormElement>
                        {activationError && (
                            <FormHelperText error={appTranslate<string>(`errors.general.${activationError}`)} />
                        )}
                    </Control>
                    <Resend
                        email={email}
                        setShowResendSuccess={setShowResendSuccess}
                        showResendSuccess={showResendSuccess}
                    />
                </Stack>
            </Container>

            {isOnboarding ? (
                <IdentificationOnboardingFormFooter>
                    <Container marginLeft={{ default: '0', md: 'auto' }}>{skipButton}</Container>
                    <Container marginLeft={{ default: 'auto', md: '0' }}>{submitButton}</Container>
                </IdentificationOnboardingFormFooter>
            ) : (
                <IdentificationFormFooter>
                    {skipButton}
                    {!isSkippable && (
                        <Button
                            href={pathfinder('homepage', {}, { query: { source: 'identification_activation_modal' } })}
                            onClick={handleGoToHomepage}
                            variant="ghost"
                            className={classes.skipButton}
                        >
                            {appTranslate<string>(`activation.back_to_homepage`)}
                        </Button>
                    )}
                    {submitButton}
                </IdentificationFormFooter>
            )}
        </Container>
    );
};

export default CodeActivationStep;
